import React, { useMemo, useState } from 'react';
import { Modal, Popover, message } from 'antd';
import { connect } from 'react-redux';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { isAllowedToApproveEntityRequests } from '../../utils/AuthUtils';
import {
	buildEntityRequestTierView,
	describeEntityRequestApprover,
	ENTITY_REQUEST_GATE_BLOCKERS,
	firstPendingEntityRequestTier,
	getEntityRequestApproveGate,
	getEntityRequestRejectGate,
	getEntityRequestTierDisplayName,
	isEntityRequestApprovalHierarchyEnforced,
	pendingEntityRequestApprovers,
	willEntityRequestBeFullyApproved,
} from '../../utils/EntityRequestApprovalUtils';
import { withUserType } from '../withUserType';
import OWButton from '../ow_button/OWButton';
import ApproveEntityAddRequestModal from '../approve_entity_add_request_modal/ApproveEntityAddRequestModal';
import RejectEntityAddRequestModal from './RejectEntityAddRequestModal';
import EntityAddRequestDetails from './EntityAddRequestDetails';
import ApprovalHierarchyDisplayEntityRequest from '../approval_hierarchy_display_entity_request/ApprovalHierarchyDisplayEntityRequest';
import { approveEntityAddRequest } from '../../thunks/entity_add_requests_thunks';

/**
 * Approver-facing review of one Suggested Entity request.
 *
 * With an `entityRequest` approval hierarchy configured, the footer actions are gated by it exactly
 * as the invoice and quote displays gate theirs — the acting user must be an approver, must not have
 * signed already, and (under sequential enforcement) must not be waiting on an earlier tier — and a
 * "Pending Approvers" link opens the shared tier-progress modal. Requests in companies with no
 * hierarchy keep the original flow: the flat canApproveEntityRequests permission decides, and both
 * actions are simply available.
 *
 * Approving is two different operations depending on where in the hierarchy the user sits. The final
 * approval materializes the supplier, so it opens the "Add Supplier" wizard where the approver can
 * amend the payload and resolve duplicates. An earlier tier's approval only records a signature, so
 * it fires the approve endpoint directly — showing that approver a supplier-creation form would
 * promise something their approval does not do.
 */
function ReviewEntityAddRequestModal({
	visible,
	record,
	hideModal,
	onSuccess,
	currentUser,
	companyConfig,
	userType,
	approveRequest,
}) {
	const [approveVisible, setApproveVisible] = useState(false);
	const [rejectVisible, setRejectVisible] = useState(false);
	const [approving, setApproving] = useState(false);

	const showApprove = () => setApproveVisible(true);
	const hideApprove = () => setApproveVisible(false);
	const showReject = () => setRejectVisible(true);
	const hideReject = () => setRejectVisible(false);

	const handleSubStepSuccess = () => {
		setApproveVisible(false);
		setRejectVisible(false);
		onSuccess();
		hideModal();
	};

	const isApprovalHierarchyEnforced = isEntityRequestApprovalHierarchyEnforced(record);
	const currentUserEmail = nullSafeGet('email', currentUser);
	const sequentialApprovalEnforcement = nullSafeGetOrElse(
		'approvalHierarchy.sequentialApprovalEnforcement',
		record,
		false
	);

	// No-hierarchy fallback: the flat permission that decided reviews before hierarchies existed.
	const defaultActionsAllowed = useMemo(
		() =>
			isAllowedToApproveEntityRequests(
				userType,
				companyConfig,
				nullSafeGetOrElse('roles', currentUser, [])
			),
		[userType, companyConfig, currentUser]
	);

	const tiers = useMemo(
		() =>
			buildEntityRequestTierView(nullSafeGetOrElse('approvalHierarchy.tierDetails', record, [])),
		[record]
	);

	const approveGate = useMemo(
		() =>
			getEntityRequestApproveGate({
				hasApprovalHierarchy: isApprovalHierarchyEnforced,
				tiers,
				currentUserEmail,
				sequentialApprovalEnforcement,
				defaultActionsAllowed,
			}),
		[
			isApprovalHierarchyEnforced,
			tiers,
			currentUserEmail,
			sequentialApprovalEnforcement,
			defaultActionsAllowed,
		]
	);

	const rejectGate = useMemo(
		() =>
			getEntityRequestRejectGate({
				hasApprovalHierarchy: isApprovalHierarchyEnforced,
				tiers,
				currentUserEmail,
				defaultActionsAllowed,
			}),
		[isApprovalHierarchyEnforced, tiers, currentUserEmail, defaultActionsAllowed]
	);

	// Without an enforcing hierarchy a single approval settles the request, so it always materializes.
	const willFullyApprove =
		!isApprovalHierarchyEnforced || willEntityRequestBeFullyApproved(tiers, currentUserEmail);

	// Records this approver's tier sign-off without touching the payload — the request stays in review
	// and the next tier is notified by the backend.
	const handleRecordTierApproval = () => {
		setApproving(true);
		approveRequest(nullSafeGet('id', record))
			.then(() => {
				message.success('Your approval has been recorded.');
				// Not clearing `approving` on success on purpose: this closes the modal, so the component
				// is gone and only the failure path has a button left to re-enable.
				handleSubStepSuccess();
			})
			.catch((error) => {
				setApproving(false);
				message.error(
					typeof error === 'string' ? error : 'Something went wrong approving this request.'
				);
			});
	};

	const pendingTier = firstPendingEntityRequestTier(tiers);
	const pendingApproverNames = pendingTier
		? pendingEntityRequestApprovers(pendingTier).map(describeEntityRequestApprover)
		: [];
	// Phrased as "next approver" rather than "waiting on": under "highest tier wins" a lower tier is not
	// a hard requirement, it is just who the escalation hands the request to next and who gets emailed.
	// Saying the request is waiting *on* them would overstate it, since the top tier can finish alone.
	const nextApproverMessage =
		isApprovalHierarchyEnforced && pendingTier
			? `Next approver: ${getEntityRequestTierDisplayName(pendingTier)}${
					pendingApproverNames.length > 0 ? ` — ${pendingApproverNames.join(', ')}` : ''
			  }`
			: undefined;

	/**
	 * A blocked action is normally still rendered, disabled, with the reason in a Popover — the invoice
	 * and quote displays behave the same way, and a button that silently vanishes leaves a user who was
	 * never eligible wondering where it went.
	 *
	 * An action the user has already taken is the exception: it is spent, not withheld, and there is
	 * nothing left to explain. The tier timeline behind "Pending Approvers" already shows their sign-off
	 * and the "Waiting on ..." line already names who the request now sits with, so keeping a dead
	 * Approve button next to that only reads as "this didn't work".
	 */
	const renderGatedButton = (gate, buttonProps, label) => {
		if (gate.allowed) {
			return <OWButton {...buttonProps}>{label}</OWButton>;
		}
		if (gate.blocker === ENTITY_REQUEST_GATE_BLOCKERS.alreadyApproved) {
			return null;
		}
		return (
			<Popover title={`Why can't I ${label.toLowerCase()}?`} content={gate.reason}>
				{/* A disabled Button swallows hover events, so the span keeps the Popover reachable. */}
				<span className="inline-flex">
					<OWButton {...buttonProps} onClick={undefined} disabled={true}>
						{label}
					</OWButton>
				</span>
			</Popover>
		);
	};

	return (
		<>
			<Modal
				visible={visible && !approveVisible && !rejectVisible}
				width="80%"
				title={`Review Request: ${nullSafeGetOrElse('displayName', record, '')}`}
				onCancel={hideModal}
				closable={true}
				destroyOnClose={true}
				footer={
					<div className="flex flex-row items-center justify-between">
						<div className="flex flex-col items-start" style={{ gap: '4px' }}>
							<ApprovalHierarchyDisplayEntityRequest record={record} />
							{nextApproverMessage ? (
								<span className="footnoteregular text-left text-character-secondary">
									{nextApproverMessage}
								</span>
							) : null}
						</div>
						<div className="flex flex-row items-center" style={{ gap: '8px' }}>
							{renderGatedButton(
								rejectGate,
								{ danger: true, size: 'large', onClick: showReject },
								'Reject'
							)}
							{renderGatedButton(
								approveGate,
								{
									type: 'primary',
									size: 'large',
									loading: approving,
									onClick: willFullyApprove ? showApprove : handleRecordTierApproval,
								},
								'Approve'
							)}
						</div>
					</div>
				}
			>
				<EntityAddRequestDetails record={record} />
			</Modal>
			{approveVisible ? (
				<ApproveEntityAddRequestModal
					visible={approveVisible}
					record={record}
					hideModal={hideApprove}
					onSuccess={handleSubStepSuccess}
				/>
			) : null}
			{rejectVisible ? (
				<RejectEntityAddRequestModal
					visible={rejectVisible}
					record={record}
					hideModal={hideReject}
					onSuccess={handleSubStepSuccess}
				/>
			) : null}
		</>
	);
}

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	approveRequest: (id) => dispatch(approveEntityAddRequest(ownProps.userType)(id, undefined)),
});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(ReviewEntityAddRequestModal)
);
